import React from 'react';

import PropTypes from 'prop-types';
import { FiArrowRight } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { trackSearchQuerySubmitted } from '~/search/actions';
import {
  CioOverlayProductsTitle,
  CioOverlayProduct,
  CioOverlayProductsSectionWrapper,
  CioOverlayProductsWrapper,
} from '~/searchCio/components/sharedComponents/productComponents';
import {
  PSRC_PREFIX_FEATURED_PRODUCTS,
  QUERY_TYPE_SEARCH_SUBMITTED,
} from '~/searchCio/constants/cioConstants';
import useCioAutosuggest from '~/searchCio/hooks/useCioAutosuggest';
import {
  constructorTextStandard,
  constructorTextHighlight,
} from '~/searchCio/utils/styles';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Router } from '~/techstyle-shared/next-routes';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useSessionActions } from '~/techstyle-shared/redux-core';

const FeaturedProductsHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ViewAllWrapper = styled.div`
  ${constructorTextStandard}
  ${constructorTextHighlight}
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: 600;
  padding: 10px;
`;

const FeaturedProductsHeader = () => {
  const secondarySite = useSecondarySite();
  const { closeOverlay, query } = useCioAutosuggest();
  const dispatch = useDispatch();
  const sessionActions = useSessionActions();

  const handleViewAllSubmit = () => {
    const routePrefix = secondarySite ? `/${secondarySite}` : '';
    const searchPath = `${routePrefix}/search`;
    const searchRoute = {
      pathname: searchPath,
      query: { q: query },
    };

    dispatch(
      trackSearchQuerySubmitted({
        query,
        queryType: QUERY_TYPE_SEARCH_SUBMITTED,
      })
    );

    sessionActions.updateSessionDetail({
      name: 'search_bar_submission',
      value: '1',
    });
    Router.push(searchRoute);
    closeOverlay();
  };

  const handleViewAllOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleViewAllSubmit();
    }
  };

  return (
    <FeaturedProductsHeaderWrapper>
      <CioOverlayProductsTitle role="heading" id="featured_results_header">
        <FormattedMessage
          id="search_autosuggest.featured_results_header"
          defaultMessage="Featured Results"
        />
      </CioOverlayProductsTitle>
      <ViewAllWrapper
        onClick={handleViewAllSubmit}
        onKeyDown={handleViewAllOnKeyDown}
        tabIndex={0}
        data-cnstrc-search-submit-btn
        data-testid="cnstrc-view-all-results"
      >
        <FormattedMessage
          id="search_autosuggest.view_all_results_label"
          defaultMessage="View All Results"
        />
        <FiArrowRight size={16} style={{ strokeWidth: 3 }} />
      </ViewAllWrapper>
    </FeaturedProductsHeaderWrapper>
  );
};

/**
 * @param {Object} products 5 for desktop, 4 for mobile.
 * Note that loading and error handling for these products should already have
 * happened beforehand (in CioDesktopOverlay for desktop or CioSearchDropdown
 * for mobile), so no skeleton loaders is required here.
 * @returns Top products that the user searched for. Also includes a link to
 * the full search page.
 */
const FeaturedProductsSection = ({ products }) => {
  const { query } = useCioAutosuggest();
  return (
    <CioOverlayProductsSectionWrapper data-testid="cnstrc-featured-products">
      <FeaturedProductsHeader />
      <CioOverlayProductsWrapper>
        {products.map((product, index) => {
          product = product.data;
          return (
            <CioOverlayProduct
              key={product.id}
              product={product}
              psrc={PSRC_PREFIX_FEATURED_PRODUCTS + query}
              dataTags={{
                'data-cnstrc-item-section': 'Products',
                'data-testid': `cnstrc-featured-product-${index}`,
                'data-sku': product.sku,
              }}
            />
          );
        })}
      </CioOverlayProductsWrapper>
    </CioOverlayProductsSectionWrapper>
  );
};

FeaturedProductsSection.propTypes = {
  products: PropTypes.object,
};

export default FeaturedProductsSection;
